import {
  useDataProvider,
  useNavigation,
  useNotification,
  useUpdate,
} from "@refinedev/core";

import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { notifyMutationError } from "@/lib/repository-utils";
import { toTranslationsMap } from "@/pages/people/schemas/person-translations";
import {
  PERSON_UPDATE_KEYS,
  PersonEditFormType,
} from "@/pages/people/schemas/person.edit.form";

export interface PersonSummary {
  id: string;
  name: string;
  knownForDepartment?: string;
  media?: { photos?: Array<{ url?: string }> };
}

/**
 * Person repository — search over the people Typesense explore endpoint via
 * Refine getList, mirroring useMovieRepository.
 */
export const usePeopleRepository = () => {
  const { getList, getOne } = useDataProvider()();
  const { mutateAsync: updateAsync } = useUpdate({ resource: "people" });
  const { edit } = useNavigation();
  const { open } = useNotification();

  async function search(query: string): Promise<PersonSummary[]> {
    if (!query) return [];
    try {
      const response = await getList({
        resource: "people",
        pagination: { currentPage: 0, pageSize: 25 },
        filters: [],
        meta: { sortBy: "_text_match:desc", query, queryBy: "name" },
      });
      return response.data.map(
        (hit: any) => hit?.document ?? hit,
      ) as PersonSummary[];
    } catch (ex) {
      console.error("people search failed due to:", ex);
      open?.({
        message: `people search failed due to ${toHumanErrorMessage(ex)}`,
        type: "error",
      });
      return [];
    }
  }

  async function get(id: string): Promise<unknown> {
    return getOne({ id, resource: "people" });
  }

  /**
   * Partial update onto `UpdatePersonCommand`: only the command's members, only the changed
   * ones. `metadata` goes as the whole map the form holds — the backend keeps the stored Tina
   * Trace stamp, which this form does not model.
   */
  async function update(values: Partial<PersonEditFormType>) {
    const payload: Record<string, unknown> = { id: values.id };
    for (const key of PERSON_UPDATE_KEYS) {
      if (values[key] !== undefined) payload[key] = values[key];
    }
    // An empty slug is a 400 ("Slug cannot be empty"), never "clear the slug" — drop it.
    if (payload.slug === "" || payload.slug === null) delete payload.slug;
    // `artistId: ""` is the unlink (both BFFs read an empty id as "no artist"); null would be
    // skipped by the backend's partial-update builder, so a cleared link maps to "".
    if (payload.artistId === null) payload.artistId = "";
    // The form edits `translations` as rows; the command takes the `{ locale: { name } }` map
    // and replaces the stored one whole.
    if (values.translations !== undefined) {
      payload.translations = toTranslationsMap(values.translations);
    }

    const response = await updateAsync?.(
      { id: values.id!, values: payload },
      {
        onSuccess: () => {
          edit("people", values.id!, "replace");
          open?.({
            type: "success",
            message: "Successfully updated person",
            description: `You updated ${values.name ?? "a person"}`,
          });
        },
        onError: (error) => {
          console.error("Failed to update person with values", values, error);
          notifyMutationError(open, "Update person request", error);
        },
      },
    );

    return response?.data.id?.toString();
  }

  return { search, get, update };
};
